import React, { useState, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import usePolicyDocumentApi from './config';
import { createPolicyDocument } from '../../services/policyService';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';
import Button from '../../components/ui/Button';
import TextInput from '../../components/ui/TextInput';
import PictureUpload from '../../components/ui/PictureUpload';

const Container = styled.div``;

const DrawerOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const DrawerContent = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 28px;
  width: 520px;
  max-width: 90vw;
`;

const FormRow = styled.div`
  margin-bottom: 16px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
`;

export default function PolicyDocumentsPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const history = useHistory();

  
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, ids: [], name: '' });
  const [deleting, setDeleting] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ title: '', description: '', category: '' });
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const { fetching, columns: baseColumns, data, pagination, _delete, refresh } = usePolicyDocumentApi(queryParams);

  const handleNewButtonClick = useCallback(() => {
    setFormData({ title: '', description: '', category: '' });
    setSelectedFile(null);
    setDrawerOpen(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!formData.title.trim() || !formData.category.trim() || !selectedFile) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('file', selectedFile);
      fd.append('title', formData.title);
      fd.append('description', formData.description);
      fd.append('category', formData.category);
      await createPolicyDocument(fd);
      setDrawerOpen(false);
      refresh();
    } catch {
      // error handled by interceptor
    } finally {
      setSaving(false);
    }
  }, [formData, selectedFile, refresh]);

  const handleDelete = useCallback((row) => {
    setDeleteConfirm({ isOpen: true, ids: [row.id], name: row.title });
  }, []);

  const handleRowsDelete = useCallback((ids) => {
    setDeleteConfirm({ isOpen: true, ids, name: `${ids.length} ${t('policy.documents').toLowerCase()}` });
  }, [t]);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    await _delete(deleteConfirm.ids);
    setDeleteConfirm({ isOpen: false, ids: [], name: '' });
    setDeleting(false);
  }, [_delete, deleteConfirm.ids]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const handleRowClick = useCallback((row) => {
    history.push(`/policy-documents/${row.id}`);
  }, [history]);

  const actionOptions = useMemo(() => {
    const options = [
      { type: 'details', label: t('policy.detail'), onClick: (row) => history.push(`/policy-documents/${row.id}`) },
    ];
    if (canWrite('ET')) {
      options.push(
        { type: 'divider' },
        { type: 'delete', label: t('policy.delete'), onClick: handleDelete },
      );
    }
    return options;
  }, [t, canWrite, handleDelete, history]);

  const columns = useMemo(() => {
    if (actionOptions.length === 0) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            return <OptionsMenu actions={actionOptions} row={row} />;
          },
        },
      },
    ];
  }, [baseColumns, actionOptions, data]);

  return (
    <Container>
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('policy.deleteTitle')}
        message={t('policy.deleteMessage', { name: deleteConfirm.name })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, ids: [], name: '' })}
        loading={deleting}
      />

      {drawerOpen && (
        <DrawerOverlay onClick={() => !saving && setDrawerOpen(false)}>
          <DrawerContent onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontFamily: "'Nunito Sans', sans-serif", fontSize: 16, fontWeight: 700, marginBottom: 20, color: '#004242' }}>
              {t('policy.new')}
            </h3>
            <FormRow>
              <TextInput
                label={t('policy.documentTitle')}
                value={formData.title}
                onChange={(e) => setFormData((p) => ({ ...p, title: e.target.value }))}
                required
              />
            </FormRow>
            <FormRow>
              <TextInput
                label={t('policy.category')}
                value={formData.category}
                onChange={(e) => setFormData((p) => ({ ...p, category: e.target.value }))}
                required
              />
            </FormRow>
            <FormRow>
              <TextInput
                label={t('policy.description')}
                value={formData.description}
                onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
                type="textarea"
                rows={3}
              />
            </FormRow>
            <FormRow>
              <PictureUpload
                label={t('policy.file')}
                value={selectedFile}
                onChange={(file) => setSelectedFile(file)}
                accept=".pdf"
                validTypes={['application/pdf']}
                maxSize={20}
                shape="square"
              />
            </FormRow>
            <Footer>
              <Button variant="ghost" onClick={() => setDrawerOpen(false)} disabled={saving}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleSave} disabled={!formData.title.trim() || !formData.category.trim() || !selectedFile || saving}>
                {saving ? '...' : t('common.save')}
              </Button>
            </Footer>
          </DrawerContent>
        </DrawerOverlay>
      )}

      {useMemo(() => (
        <DataTable
          title={t('policy.documents')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={canWrite('ET')}
          onNewButtonClick={handleNewButtonClick}
          onRowsDelete={canWrite('ET') ? handleRowsDelete : undefined}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
          onRowClick={handleRowClick}
        />
      ), [
        t, fetching, columns, data, pagination, canWrite,
        handleNewButtonClick, handleRowsDelete, filterList,
        handleFilterConfirm, handleDataStateChange, handleRowClick,
      ])}
    </Container>
  );
}
